/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import {
  DueDiligenceFluentIds,
  AccountActivityQuestionIds,
  getStringSettingFluentId,
  ProductFluentIds,
  SupportContactFluentIds,
  SupportFaqFluentIds,
  SupportLinksFluentIds,
  OaoDisclosuresFluentIds,
  AccountEligibilityIds,
  InstitutionFeatureFluentIds,
  MarketingCampaignFluentIds,
  OAuthApplicationFluentIds,
  PromoFluentIds,
  PromptGroupFluentIds,
  PromptConfigurationFluentIds,
} from "./SettingFluentIds";

import type { ReactLocalization } from "@fluent/react";
import type {
  OaoProduct,
  ProductDisclosure,
  SupportContact,
  SupportFaqCategory,
  OaoDisclosure,
  SupportLink,
  AccountEligibilityQuestion,
  AccountEligibilityAnswer,
  OAuthApplication,
} from "../types/SettingLocalizers.types";
import type { DueDiligenceQuestion } from "../types";
import type { RenderItemMenuProps } from "../types/Institution.types";

const SETTINGS_LOCALE_SUFFIX = "-settings";

// A `Map`, but one that drops entries from memory when all strong references to a key are gone.
// `ReactLocalization` instances change when the locale changes, and this acts as our cache key.
// <https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/WeakMap>
const l10nCache = new WeakMap<ReactLocalization, boolean>();

/**
 * Checks if settings must be translated from the current `l10n` instance.
 * @param l10n - the current ReactLocalization instance
 * @returns Boolean
 */
export const hasSettingTranslations = (l10n: ReactLocalization): boolean => {
  const cached = l10nCache.get(l10n); // same instance or undefined
  if (cached !== undefined) {
    return cached;
  }
  const bundles = Array.from(l10n.bundles);
  if (bundles.length === 0) {
    l10nCache.set(l10n, false);
    return false;
  }
  const currentLocale = bundles[0]?.locales[0];
  const isEnglish = currentLocale === "en";
  const hasSettingsLocale = bundles.some((bundle) =>
    bundle.locales[0].includes(SETTINGS_LOCALE_SUFFIX),
  );
  const result = !isEnglish && hasSettingsLocale;
  l10nCache.set(l10n, result);
  return result;
};

/**
 * Translates a string type setting
 * @usage
 * Takes `SETTING_NAME` as defined in python as `setting-name`
 * ```
 * translateStringSetting("institution-announcement", "My announcement!", l10n)
 * ```
 */
export const translateStringSetting = (
  settingName: string,
  settingValue: string = "",
  l10n: ReactLocalization,
): string => {
  if (!settingValue || !hasSettingTranslations(l10n)) {
    return settingValue;
  }

  const translatedString = l10n.getString(
    getStringSettingFluentId(settingName),
    null,
    settingValue,
  );

  return translatedString;
};
/**
 * @usage
 * ```
 * // single product
 * const myLocalizedProduct = getLocalizedProduct(product, l10n);
 *
 * // multiple products
 * ```
 * products.map((p) => getLocalizedProduct(p, l10n))
 * ```
 */
export const translateProduct = <T extends OaoProduct>(
  product: T,
  l10n: ReactLocalization,
): T => {
  const localizedProduct = { ...product };
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new ProductFluentIds(product.id);

    localizedProduct.name = l10n.getString(ids.getName(), null, product.name);

    localizedProduct.description = l10n.getString(
      ids.getDescription(),
      null,
      product.description,
    );

    if (localizedProduct.details) {
      localizedProduct.details = localizedProduct.details.map((detail, i) =>
        detail ? l10n.getString(ids.getDetail(i), null, detail) : detail,
      );
    }

    if (product.more_details_copy) {
      localizedProduct.more_details_copy = l10n.getString(
        ids.getMoreDetailsCopy(),
        null,
        product.more_details_copy,
      );
    }

    if (localizedProduct.details) {
      localizedProduct.details = localizedProduct.details.map((detail, i) =>
        detail ? l10n.getString(ids.getDetail(i), null, detail) : detail,
      );
    }

    if (localizedProduct.disclosures) {
      localizedProduct.disclosures = localizedProduct.disclosures.map(
        (disclosure, i) => {
          const translatedDisclosure: ProductDisclosure = {};
          if (disclosure.checkbox_text) {
            translatedDisclosure.checkbox_text = l10n.getString(
              ids.getDisclosureCheckboxText(i),
              null,
              disclosure.checkbox_text,
            );
          }
          if (disclosure.markdown) {
            translatedDisclosure.markdown = l10n.getString(
              ids.getDisclosureMarkdown(i),
              null,
              disclosure.markdown,
            );
          }
          if (disclosure.links) {
            translatedDisclosure.links = disclosure.links.map(
              ({ text, url, urls }, linkIndex) => ({
                text: l10n.getString(
                  ids.getDisclosureLinkText(i, linkIndex),
                  null,
                  text,
                ),
                url,
                urls,
              }),
            );
          }
          return translatedDisclosure;
        },
      );
    }

    if (localizedProduct.monthly_service_fee) {
      localizedProduct.monthly_service_fee = l10n.getString(
        ids.getMonthlyServiceFee(),
        null,
        product.monthly_service_fee,
      );
    }
  }

  return localizedProduct;
};

export const translateDueDiligenceQuestions = <
  T extends DueDiligenceQuestion[],
>(
  questions: T,
  aoType: "cao" | "bao" | "consumer-slao" | "business-slao",
  l10n: ReactLocalization,
): T => {
  const shouldLocalize = hasSettingTranslations(l10n);
  if (!shouldLocalize) {
    return questions;
  }

  const institutionIds = new DueDiligenceFluentIds(aoType);

  // Track institution question index separately from product questions
  let institutionIndex = 0;
  // Track product question indices per product
  const productIndices = new Map<string, number>();

  const localizedQuestions = questions.map((question) => {
    if (question.productId != null) {
      const productId = String(question.productId);
      const ids = new AccountActivityQuestionIds(productId);
      const qIndex = productIndices.get(productId) ?? 0;
      productIndices.set(productId, qIndex + 1);
      return translateSingleActivityQuestion(question, qIndex, ids, l10n);
    }
    const result = translateSingleActivityQuestion(
      question,
      institutionIndex,
      institutionIds,
      l10n,
    );
    institutionIndex += 1;
    return result;
  });

  return localizedQuestions as T;
};

/**
 * Fluent ID getter interface shared by DueDiligenceFluentIds and AccountActivityQuestionIds
 */
interface ActivityQuestionIdGetter {
  getQuestion(questionIndex: number): string;
  getQuestionLabel(questionIndex: number): string;
  getDropdownQuestionOption(questionIndex: number, optionIndex: number): string;
  getAnswerOptionDisplayText(
    questionIndex: number,
    optionIndex: number,
  ): string;
  getFollowUpQuestionPrefix(
    questionIndex: number,
    optionIndex: number,
    followUpIndex: number,
  ): string;
}

/**
 * Recursively translates a single activity question using the provided ID getter.
 */
const translateSingleActivityQuestion = (
  question: DueDiligenceQuestion,
  index: number,
  ids: ActivityQuestionIdGetter,
  l10n: ReactLocalization,
): DueDiligenceQuestion => {
  const localized = {
    ...question,
    rawQuestion: question.rawQuestion || question.question,
  };

  localized.question = l10n.getString(
    ids.getQuestion(index),
    null,
    question.question,
  );

  const { questionType } = question;

  if (
    (questionType === "dropdown" || questionType === "freeform") &&
    question.label
  ) {
    localized.label = l10n.getString(
      ids.getQuestionLabel(index),
      null,
      question.label,
    );
  }

  if (questionType === "dropdown" && question.options) {
    localized.rawOptions = question.rawOptions || question.options;
    localized.options = question.options.map((option, optionIndex) =>
      l10n.getString(
        ids.getDropdownQuestionOption(index, optionIndex),
        null,
        option,
      ),
    );
  }

  if (question.answerOptions) {
    localized.answerOptions = question.answerOptions.map(
      (answerOption, optionIndex) => {
        const localizedOption = {
          ...answerOption,
          value: answerOption.value || answerOption.displayText || "",
        };

        if (answerOption.displayText != null) {
          localizedOption.displayText = l10n.getString(
            ids.getAnswerOptionDisplayText(index, optionIndex),
            null,
            answerOption.displayText,
          );
        }
        if (answerOption.followUpQuestions) {
          localizedOption.followUpQuestions =
            answerOption.followUpQuestions.map((followUp, followUpIndex) => {
              // Build a nested ID getter that prepends the follow-up prefix
              const followUpPrefix = ids.getFollowUpQuestionPrefix(
                index,
                optionIndex,
                followUpIndex,
              );
              const nestedIds: ActivityQuestionIdGetter = {
                getQuestion: () => followUpPrefix,
                getQuestionLabel: () => `${followUpPrefix}-label`,
                getDropdownQuestionOption: (_, oi) =>
                  `${followUpPrefix}-options${oi}`,
                getAnswerOptionDisplayText: (_, oi) =>
                  `${followUpPrefix}-answer-options${oi}-display-text`,
                getFollowUpQuestionPrefix: (_, oi, fi) =>
                  `${followUpPrefix}-answer-options${oi}-follow-up${fi}`,
              };
              return translateSingleActivityQuestion(
                followUp,
                0,
                nestedIds,
                l10n,
              );
            });
        }

        return localizedOption;
      },
    );
  }

  return localized;
};

/**
 * @usage
 * ```
 * const translatedContacts = translateSupportContacts(contacts, l10n)
 * ```
 */
export const translateSupportContacts = <T extends SupportContact[]>(
  contacts: T,
  l10n: ReactLocalization,
): T => {
  let localizedContacts = [...contacts];
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new SupportContactFluentIds();

    localizedContacts = contacts.map((contact, i) => {
      contact.title = l10n.getString(ids.getTitle(i), null, contact.title);
      if (contact.numbers) {
        contact.numbers.map((number, numberIdx) => {
          number.name = l10n.getString(
            ids.getNumberName(i, numberIdx),
            null,
            number.name,
          );
          return number;
        });
      }
      return contact;
    });
  }

  return localizedContacts as T;
};

export const translateFaqQuestions = <T extends SupportFaqCategory[]>(
  categories: T,
  l10n: ReactLocalization,
): T => {
  const localizedCategories = [...categories];
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new SupportFaqFluentIds();

    localizedCategories.forEach((category, i) => {
      const currentCategory = localizedCategories[i];
      currentCategory.category = l10n.getString(
        ids.getCategory(i),
        null,
        category.category,
      );

      (currentCategory.questions || []).forEach(
        (_: any, questionIndex: number) => {
          const currentQuestion = currentCategory.questions[questionIndex];
          currentQuestion.question = l10n.getString(
            ids.getQuestion(i, questionIndex),
            null,
            currentQuestion.question,
          );
          currentQuestion.answer = l10n.getString(
            ids.getAnswer(i, questionIndex),
            null,
            currentQuestion.answer,
          );
        },
      );
    });
  }

  return localizedCategories as T;
};

export const translateSupportLinks = <T extends SupportLink[]>(
  links: T,
  l10n: ReactLocalization,
): T => {
  let localizedLinks = [...links];
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new SupportLinksFluentIds();
    localizedLinks = links.map(({ text }, i) => ({
      ...links[i],
      text: l10n.getString(ids.getText(i), null, text),
    }));
  }

  return localizedLinks as T;
};

/**
 * @usage
 * ```
 * const translatedDisclosures = translateOaoDisclosures(disclosures, l10n)
 * ```
 */
export const translateOaoDisclosures = <T extends OaoDisclosure[]>(
  disclosures: T,
  l10n: ReactLocalization,
): T => {
  let localizedDisclosures = [...disclosures];
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new OaoDisclosuresFluentIds();

    localizedDisclosures = disclosures.map((disclosure, i) => {
      if (disclosure.markdown) {
        disclosure.markdown = l10n.getString(
          ids.getMarkdown(i),
          null,
          disclosure.markdown,
        );
      }
      if (disclosure.checkbox_text) {
        disclosure.checkbox_text = l10n.getString(
          ids.getCheckboxText(i),
          null,
          disclosure.checkbox_text,
        );
      }
      if (disclosure.links) {
        disclosure.links.map((link, linkIndex) => {
          link.text = l10n.getString(
            ids.getLinkText(i, linkIndex),
            null,
            link.text,
          );
          return link;
        });
      }
      return disclosure;
    });
  }

  return localizedDisclosures as T;
};

export const translateEligibilityQuestion = <
  T extends AccountEligibilityQuestion,
>(
  question: T,
  l10n: ReactLocalization,
): T => {
  const localizedQuestion = { ...question };
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new AccountEligibilityIds("question");
    localizedQuestion.display_text = l10n.getString(
      ids.getDisplayText(localizedQuestion.id),
      null,
      localizedQuestion.display_text,
    );
  }
  return localizedQuestion;
};

export const translateEligibilityAnswer = <T extends AccountEligibilityAnswer>(
  answer: T,
  l10n: ReactLocalization,
): T => {
  const localizedAnswer = { ...answer };
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new AccountEligibilityIds("answer");
    localizedAnswer.display_text = l10n.getString(
      ids.getDisplayText(localizedAnswer.id),
      null,
      localizedAnswer.display_text,
    );
  }
  return localizedAnswer;
};

export const translateTopMenuItems = <T extends API.AnyMenuItem>(
  menuItems: RenderItemMenuProps<T>[],
  l10n: ReactLocalization,
): RenderItemMenuProps<T>[] => {
  if (!menuItems?.length) return [];

  let localizedMenuItems = [...menuItems];
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new InstitutionFeatureFluentIds();
    localizedMenuItems = menuItems.map((item) => ({
      ...item,
      name: l10n.getString(ids.getTopMenuItem(item.name), null, item.name),
    }));
  }
  return localizedMenuItems;
};

export const translateSidebarMenuItems = <
  T extends API.SidebarMenuItem_Resolved,
>(
  sidebarMenuItems: T[],
  l10n: ReactLocalization,
): T[] => {
  if (!sidebarMenuItems?.length) return [];

  let localizedMenuItems = [...sidebarMenuItems];
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new InstitutionFeatureFluentIds();
    localizedMenuItems = sidebarMenuItems.map((item) => ({
      ...item,
      title: l10n.getString(
        ids.getSidebarMenuItem(item.title),
        null,
        item.title,
      ),
      subItems: item.subItems?.map((subItem) => ({
        ...subItem,
        title: subItem.title
          ? l10n.getString(
              ids.getSidebarMenuItem(subItem.title),
              null,
              subItem.title,
            )
          : subItem.title,
      })),
    }));
  }
  return localizedMenuItems;
};

/**
 * @usage
 * ```
 * const translatedCampaign = translateMarketingCampaign(campaign, l10n)
 * ```
 */
export const translateMarketingCampaign = <
  T extends {
    uuid: string;
    asset_config?: { title?: string; body?: string; linkText?: string };
  },
>(
  campaign: T,
  l10n: ReactLocalization,
): T => {
  const localizedCampaign = { ...campaign };
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize && campaign.asset_config) {
    const ids = new MarketingCampaignFluentIds(campaign.uuid);

    if (campaign.asset_config.title) {
      localizedCampaign.asset_config = {
        ...localizedCampaign.asset_config,
        title: l10n.getString(
          ids.getTitle(),
          null,
          campaign.asset_config.title,
        ),
      };
    }

    if (campaign.asset_config.body) {
      localizedCampaign.asset_config = {
        ...localizedCampaign.asset_config,
        body: l10n.getString(ids.getBody(), null, campaign.asset_config.body),
      };
    }

    if (campaign.asset_config.linkText) {
      localizedCampaign.asset_config = {
        ...localizedCampaign.asset_config,
        linkText: l10n.getString(
          ids.getLinkText(),
          null,
          campaign.asset_config.linkText,
        ),
      };
    }
  }

  return localizedCampaign;
};

/**
 * @usage
 * ```
 * const translateOffer = translateOffer(offer, l10n)
 * ```
 */
export const translateOffer = <
  T extends {
    title: string;
    description: string;
    action_text?: string;
    index?: number;
  },
>(
  offer: T,
  l10n: ReactLocalization,
): T => {
  const localizedOffer = { ...offer };
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new PromoFluentIds(offer.index || 0);

    localizedOffer.title = l10n.getString(ids.getTitle(), null, offer.title);

    localizedOffer.description = l10n.getString(
      ids.getDescription(),
      null,
      offer.description,
    );

    if (offer.action_text) {
      localizedOffer.action_text = l10n.getString(
        ids.getActionText(),
        null,
        offer.action_text,
      );
    }
  }

  return localizedOffer;
};

/**
 * @usage
 * ```
 * const translatedOAuthApp = translateOAuthApplication(oauthApp, l10n)
 * ```
 */
export const translateOAuthApplication = <T extends OAuthApplication>(
  oauthApp: T,
  l10n: ReactLocalization,
): T => {
  const localizedOAuthApp = { ...oauthApp };
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new OAuthApplicationFluentIds(oauthApp.uuid);

    if (oauthApp.name) {
      localizedOAuthApp.name = l10n.getString(
        ids.getName(),
        null,
        oauthApp.name,
      );
    }

    if (oauthApp.tagline) {
      localizedOAuthApp.tagline = l10n.getString(
        ids.getTagline(),
        null,
        oauthApp.tagline,
      );
    }

    if (oauthApp.description) {
      localizedOAuthApp.description = l10n.getString(
        ids.getDescription(),
        null,
        oauthApp.description,
      );
    }
  }

  return localizedOAuthApp;
};

/**
 * Translates a prompt group's title and description
 * @usage
 * ```
 * const translated = translatePromptGroup({ id: "some-uuid", title: "Get started", description: "Set up your account" }, l10n)
 * ```
 */
export const translatePromptGroup = <
  T extends { id: string; title: string; description: string },
>(
  promptGroup: T,
  l10n: ReactLocalization,
): T => {
  const localized = { ...promptGroup };
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new PromptGroupFluentIds(promptGroup.id);

    if (promptGroup.title) {
      localized.title = l10n.getString(
        ids.getExternalTitle(),
        null,
        promptGroup.title,
      );
    }

    if (promptGroup.description) {
      localized.description = l10n.getString(
        ids.getDescription(),
        null,
        promptGroup.description,
      );
    }
  }

  return localized;
};

/**
 * Translates a prompt configuration's title, description, and link text
 * @usage
 * ```
 * const translated = translatePromptConfiguration({ id: "some-uuid", title: "Link account", description: "Connect your bank", link_text: null }, l10n)
 * ```
 */
export const translatePromptConfiguration = <
  T extends {
    id: string;
    title: string;
    description: string;
    link_text: string | null;
  },
>(
  prompt: T,
  l10n: ReactLocalization,
): T => {
  const localized = { ...prompt };
  const shouldLocalize = hasSettingTranslations(l10n);

  if (shouldLocalize) {
    const ids = new PromptConfigurationFluentIds(prompt.id);

    if (prompt.title) {
      localized.title = l10n.getString(ids.getTitle(), null, prompt.title);
    }

    if (prompt.description) {
      localized.description = l10n.getString(
        ids.getDescription(),
        null,
        prompt.description,
      );
    }

    if (prompt.link_text) {
      localized.link_text = l10n.getString(
        ids.getLinkText(),
        null,
        prompt.link_text,
      );
    }
  }

  return localized;
};
